import { useState, useEffect } from "react";
import { usePlaybooks, useActivatePlaybook } from "../hooks/usePlaybooks.ts";
import { useActiveBrand } from "../hooks/useActiveBrand.ts";
import { PlaybookCard } from "../components/playbooks/PlaybookCard.tsx";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader.tsx";
import { LoadErrorState } from "../components/ui/LoadState";

export function Playbooks() {
  const { data: playbooksData, isLoading: playbooksLoading, isError: playbooksFailed, refetch: refetchPlaybooks } = usePlaybooks();
  const { brands, activeBrandId, isLoading: brandsLoading } = useActiveBrand();
  const activatePlaybook = useActivatePlaybook();

  const [selectedBrandId, setSelectedBrandId] = useState<string>("");
  const [activatingId, setActivatingId] = useState<string | null>(null);

  useEffect(() => {
    if (activeBrandId) {
      setSelectedBrandId(activeBrandId);
    }
  }, [activeBrandId]);

  const isLoading = playbooksLoading || brandsLoading;
  const playbooks = playbooksData?.data || [];

  const handleActivate = async (playbookId: string) => {
    if (!selectedBrandId) return;
    setActivatingId(playbookId);
    try {
      await activatePlaybook.mutateAsync({
        playbookId,
        brand_id: selectedBrandId,
      });
    } finally {
      setActivatingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        eyebrow="Operator Workspace"
        title="Growth Playbooks"
        description="Pre-built content strategies to grow your audience. Select a brand and activate a playbook to auto-schedule daily content."
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Playbooks" },
        ]}
      />

      {/* Brand selector */}
      <div className="max-w-xs">
        <label
          htmlFor="brand-select"
          className="mb-1.5 block text-sm font-medium text-slate-700"
        >
          Select Brand
        </label>
        <select
          id="brand-select"
          value={selectedBrandId}
          onChange={(e) => setSelectedBrandId(e.target.value)}
          className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">Choose a brand...</option>
          {brands.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.name}
            </option>
          ))}
        </select>
      </div>

      {/* No brand selected warning */}
      {!selectedBrandId && playbooks.length > 0 && (
        <p className="text-sm text-amber-600">
          Select a brand above before activating a playbook.
        </p>
      )}

      {/* Playbook grid */}
      {playbooksFailed && !playbooksData ? (
        <LoadErrorState title="Playbooks could not be loaded" onRetry={() => void refetchPlaybooks()} />
      ) : playbooks.length === 0 ? (
        <div className="flex min-h-[30vh] flex-col items-center justify-center rounded-[20px] border border-dashed border-slate-300 bg-white p-8 shadow-sm">
          <p className="mb-1 text-sm font-medium text-slate-500">
            No playbooks available
          </p>
          <p className="text-xs text-slate-400">
            Ask your admin to seed playbooks, or check back later.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {playbooks.map((playbook) => (
            <PlaybookCard
              key={playbook.id}
              playbook={playbook}
              onActivate={handleActivate}
              isActivating={activatingId === playbook.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
